import { ApplicationConfig, APP_INITIALIZER, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeEn from '@angular/common/locales/en';
registerLocaleData(localeRu);
registerLocaleData(localeEn);
import { RouteReuseStrategy, provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { cabinetInterceptor } from './core/interceptors/cabinet.interceptor';
import { companyInterceptor } from './core/interceptors/company.interceptor';
import { errorCaptureInterceptor } from './core/interceptors/error-capture.interceptor';
import { diagnosticNetworkInterceptor } from './core/interceptors/diagnostic-network.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { missingCredentialInterceptor } from './core/interceptors/missing-credential.interceptor';
import { GlobalErrorHandler } from './core/services/error-capture.service';
import { environment } from '../environments/environment';
import { SameUrlRouteReuseStrategy } from './core/routing/same-url-route-reuse.strategy';

// Keycloak (conditional)
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { initializeKeycloak } from './core/auth/keycloak-init.factory';
import { AuthService } from './core/services/auth.service';

// i18n
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

// Feature Flags
import { FeatureFlagsService } from './core/services/feature-flags.service';

// PrimeNG
import { MessageService } from 'primeng/api';

// Store - ONLY Core State (auth + webSocket)
import { authReducer } from './store/reducers/auth.reducer';
import { webSocketReducer, realTimePriceReducer } from './store/reducers/websocket.reducer';
import { WebSocketEffects } from './store/effects/websocket.effects';

const SUPPORTED_LANGS = ['ru', 'en'];
const DEFAULT_LANG = 'ru';
const LANG_STORAGE_KEY = 'app_language';

/**
 * APP_INITIALIZER factory: устанавливает язык приложения до первого рендера.
 *
 * Стратегия (в порядке приоритета):
 * 1. localStorage('app_language') — быстро, без сети, работает при каждой загрузке
 * 2. GET /users/settings/general — для первого входа пользователя
 *    - В Keycloak режиме этот вызов происходит до KC аутентификации → возвращает 401
 *    - catchError обрабатывает 401 gracefully, возвращает DEFAULT_LANG
 *    - При последующих загрузках localStorage fast path обходит этот вызов
 * 3. DEFAULT_LANG ('ru') — финальный fallback
 */
export function initLanguage(translate: TranslateService, http: HttpClient) {
  return async () => {
    // Fast path: read from localStorage (avoids API call on each reload)
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) {
      await firstValueFrom(translate.use(stored));
      return;
    }

    // Fallback: load from user settings API
    try {
      const settings = await firstValueFrom(
        http.get<{ language?: string }>(`${environment.apiUrl}/users/settings/general`).pipe(
          catchError(() => of({ language: DEFAULT_LANG }))
        )
      );
      const lang = settings?.language && SUPPORTED_LANGS.includes(settings.language)
        ? settings.language
        : DEFAULT_LANG;
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      await firstValueFrom(translate.use(lang));
    } catch {
      await firstValueFrom(translate.use(DEFAULT_LANG));
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    { provide: RouteReuseStrategy, useClass: SameUrlRouteReuseStrategy },

    // HTTP: authInterceptor теперь активен В ОБОИХ режимах (fix
    // auth-401-zombie) — раньше в KC mode Bearer добавлял сторонний
    // class-based KeycloakBearerInterceptor (via withInterceptorsFromDi()),
    // который на неудачном refresh просто слал запрос без Authorization
    // (анонимный 401 в логах, "зомби-сессия" при видимо авторизованной
    // шапке). Наш authInterceptor делает silent refresh ПРОАКТИВНО до
    // отправки и уводит на переавторизацию при неудаче — KeycloakBearerInterceptor
    // и withInterceptorsFromDi() больше не нужны, убраны.
    // Порядок важен: auth (Bearer/refresh) ДО error (401-retry/toast).
    // cabinetInterceptor добавляет X-Cabinet-ID ВСЕГДА (в обоих режимах).
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        cabinetInterceptor,
        companyInterceptor,            // Story 110.7: X-Company-ID org-focus header
        missingCredentialInterceptor,  // Story 87.22: 412 → modal
        errorInterceptor,
        errorCaptureInterceptor,
        diagnosticNetworkInterceptor,
      ]),
    ),
    provideAnimations(),

    // i18n (ngx-translate)
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'ru',
      })
    ),
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),

    // i18n: language init before render (AC-1, AC-2)
    {
      provide: APP_INITIALIZER,
      useFactory: initLanguage,
      deps: [TranslateService, HttpClient],
      multi: true,
    },

    // Keycloak providers (только при useKeycloakAuth=true)
    // Bearer-интерсептор больше не регистрируется здесь (fix
    // auth-401-zombie) — эту роль теперь берёт на себя authInterceptor выше.
    ...(environment.useKeycloakAuth
      ? [
          importProvidersFrom(KeycloakAngularModule),
          KeycloakService,
          {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
            // Fix auth-401-zombie r4: AuthService — чтобы markAuthenticated()
            // защёлкнуть факт успешной bootstrap-аутентификации ЭАГЕРЛИ.
            deps: [KeycloakService, AuthService],
          },
        ]
      : []),

    // NGRX Store - Core State (auth + webSocket)
    // WebSocket is global (used across features), so kept in root
    // Feature states (dashboard, analytics, products, pricingRules) are lazy loaded
    provideStore({
      auth: authReducer,
      webSocket: webSocketReducer,
      realTimePrice: realTimePriceReducer
    }),

    // NGRX Effects - Core Effects (WebSocket)
    // Feature effects (Dashboard, Analytics, Product) moved to lazy-loaded features
    provideEffects([WebSocketEffects]),

    // DevTools — исключён из prod/beta бандла (BR-5: bundle optimization)
    ...(isDevMode() ? [provideStoreDevtools({ maxAge: 25, autoPause: true })] : []),

    // PrimeNG Services
    MessageService,

    // Feature flags: load before render (fail-safe, never throws)
    {
      provide: APP_INITIALIZER,
      useFactory: (flags: FeatureFlagsService) => () => flags.load(),
      deps: [FeatureFlagsService],
      multi: true,
    },

    // Global Error Handler (Story 15.12 - Feedback Collection)
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // REMOVED: provideEcharts() - moved to analytics feature (lazy loaded)
    // This saves 4.6MB in initial bundle!
  ]
};
